import React, { useEffect, useState } from 'react';
import { Box, Button, Drawer, IconButton, Popover, TextField, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import EditCalendarOutlinedIcon from '@mui/icons-material/EditCalendarOutlined';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StatusPill } from '../common/ui';
import { fmtDate, fmtDateWith, fmtTimeRange } from '../../utils/format';
import { fieldStepOf } from '../../utils/workflows';
import { navigationUrl } from '../../utils/geo';

function Line({ icon, children }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, py: 0.6 }}>
            <Box sx={{ color: '#94A3B8', display: 'flex', pt: 0.15 }}>{icon}</Box>
            <Box sx={{ fontSize: 14, color: '#1E293B', minWidth: 0, wordBreak: 'break-word' }}>{children}</Box>
        </Box>
    );
}

/**
 * Quick view of a planner job (tap or click on a block, chip or card): what is needed on site, a large
 * "field work done", open the job, and change the slot (the touch alternative to drag and drop).
 * Bottom sheet on phones, a card next to the job on larger screens.
 */
export default function JobPeek({ job, anchorEl, onClose, onFieldDone, onSchedule }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const phone = useMediaQuery(theme.breakpoints.down('sm'));
    const [editing, setEditing] = useState(false);
    const [slot, setSlot] = useState({ date: '', start: '', end: '' });

    useEffect(() => {
        setEditing(false);
        if (job) setSlot({ date: job.scheduledDate || '', start: job.scheduledStart || '', end: job.scheduledEnd || '' });
    }, [job]);

    if (!job) return null;
    const step = fieldStepOf(job.category);
    const fieldDone = job.fieldDone && job.status !== 'DONE' && step ? job.steps?.[step] : null;
    const badTime = slot.start && slot.end && slot.end <= slot.start;
    const when = job.scheduledDate
        ? `${fmtDateWith(new Date(job.scheduledDate + 'T00:00'), { weekday: 'long', day: 'numeric', month: 'long' })}${job.scheduledStart ? `, ${fmtTimeRange(job)}` : ''}`
        : t('jobs.notScheduled');

    const saveSlot = () => {
        onSchedule(job, slot.date || null, slot.date && slot.start ? slot.start : null, slot.date && slot.start && slot.end ? slot.end : null);
        onClose();
    };

    const body = (
        <Box sx={{ width: phone ? 'auto' : 360, p: 2.25, pb: phone ? 3 : 2.25 }}>
            {phone && <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: '#E2E8F0', mx: 'auto', mt: -0.75, mb: 1.5 }} />}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ width: 4, alignSelf: 'stretch', borderRadius: 4, bgcolor: job.category?.color || '#CBD5E1', flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 12, color: '#94A3B8', fontWeight: 600 }}>
                        {[job.number, job.category?.name].filter(Boolean).join(' · ')}
                    </Typography>
                    <Typography sx={{ fontSize: 17, fontWeight: 700, color: '#0F172A', lineHeight: 1.3 }}>{job.title}</Typography>
                </Box>
                <IconButton size="small" onClick={onClose} aria-label={t('common.close')} sx={{ mt: -0.5, mr: -0.5 }}><CloseIcon fontSize="small" /></IconButton>
            </Box>

            <Box sx={{ mt: 1.5 }}>
                <Line icon={<ScheduleIcon sx={{ fontSize: 19 }} />}>{when}</Line>
                <Line icon={<PersonOutlineIcon sx={{ fontSize: 19 }} />}>{job.client?.name}</Line>
                {job.client?.phone && (
                    <Line icon={<PhoneOutlinedIcon sx={{ fontSize: 19 }} />}>
                        <Box component="a" href={`tel:${job.client.phone}`} sx={{ color: '#427898', fontWeight: 600, textDecoration: 'none' }}>{job.client.phone}</Box>
                    </Line>
                )}
                {(job.cadastralZone || job.parcelNumber) && (
                    <Line icon={<PlaceOutlinedIcon sx={{ fontSize: 19 }} />}>
                        {[job.cadastralZone, job.parcelNumber && `${t('jobs.parcelShort')} ${job.parcelNumber}`].filter(Boolean).join(' · ')}
                    </Line>
                )}
                {job.assignees?.length > 0 && (
                    <Line icon={<GroupOutlinedIcon sx={{ fontSize: 19 }} />}>{job.assignees.map(p => `${p.firstName} ${p.lastName}`).join(', ')}</Line>
                )}
                <Box sx={{ mt: 0.75 }}><StatusPill status={job.status} size="sm" /></Box>
            </Box>

            {editing ? (
                <Box sx={{ mt: 2, p: 1.5, borderRadius: '12px', bgcolor: '#F8FAFC', border: '1px solid #EEF2F6' }}>
                    <TextField fullWidth size="small" type="date" label={t('jobs.fields.scheduledDate')} value={slot.date}
                               onChange={(e) => setSlot(s => ({ ...s, date: e.target.value }))} slotProps={{ inputLabel: { shrink: true } }} />
                    <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                        <TextField fullWidth size="small" type="time" label={t('jobs.fields.scheduledStart')} value={slot.start} disabled={!slot.date}
                                   onChange={(e) => setSlot(s => ({ ...s, start: e.target.value }))} slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: 900 } }} />
                        <TextField fullWidth size="small" type="time" label={t('jobs.fields.scheduledEnd')} value={slot.end} disabled={!slot.date || !slot.start}
                                   error={!!badTime} onChange={(e) => setSlot(s => ({ ...s, end: e.target.value }))}
                                   slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: 900 } }} />
                    </Box>
                    {badTime && <Typography sx={{ fontSize: 12, color: '#DC2626', mt: 0.5 }}>{t('jobs.errors.timeRange')}</Typography>}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.5 }}>
                        <Button size="small" onClick={() => setEditing(false)} sx={{ color: '#64748B' }}>{t('common.cancel')}</Button>
                        <Button size="small" variant="contained" disableElevation onClick={saveSlot} disabled={!!badTime}>{t('common.save')}</Button>
                    </Box>
                </Box>
            ) : (
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {fieldDone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#059669', fontWeight: 700, fontSize: 14, py: 0.5 }}>
                            <CheckCircleIcon sx={{ fontSize: 20 }} />{t('planner.fieldDone')} · {fmtDate(fieldDone)}
                        </Box>
                    )}
                    {step && !job.fieldDone && (
                        <Button fullWidth size="large" variant="contained" color="success" disableElevation startIcon={<CheckCircleOutlineIcon />}
                                onClick={() => { onClose(); onFieldDone(job, step); }} sx={{ py: 1.1 }}>
                            {t('planner.markFieldDone')}
                        </Button>
                    )}
                    {job.latitude != null && (
                        <Button variant="outlined" startIcon={<DirectionsCarOutlinedIcon />} href={navigationUrl(job.latitude, job.longitude)}
                                target="_blank" rel="noopener noreferrer">{t('location.navigate')}</Button>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', '& > *': { flex: '1 1 150px', whiteSpace: 'nowrap' } }}>
                        <Button variant="outlined" startIcon={<OpenInNewIcon />} onClick={() => navigate(`/jobs/${job.id}`)}>{t('planner.openJob')}</Button>
                        <Button variant="outlined" startIcon={<EditCalendarOutlinedIcon />} onClick={() => setEditing(true)}>{t('planner.changeSlot')}</Button>
                    </Box>
                </Box>
            )}
        </Box>
    );

    return phone ? (
        <Drawer anchor="bottom" open onClose={onClose} PaperProps={{ sx: { borderTopLeftRadius: 18, borderTopRightRadius: 18, maxHeight: '90vh' } }}>
            {body}
        </Drawer>
    ) : (
        <Popover open={!!anchorEl} anchorEl={anchorEl} onClose={onClose}
                 anchorOrigin={{ vertical: 'center', horizontal: 'right' }} transformOrigin={{ vertical: 'center', horizontal: 'left' }}
                 PaperProps={{ sx: { borderRadius: '14px', boxShadow: '0 18px 40px -16px rgba(15,23,42,0.35)', ml: 1 } }}>
            {body}
        </Popover>
    );
}
